import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ImageIcon from "@mui/icons-material/Image";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MovieIcon from "@mui/icons-material/Movie";
import SendIcon from "@mui/icons-material/Send";
import { chat, ordersApi } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import Message from "./Message";
import { avatarColor, initials } from "./avatarUtils";

function ChatPanel({ phone, name, onBack, onConversationUpdate }) {
  const showToast = useToast();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const lastIdRef = useRef(0);
  const isPollingRef = useRef(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [templateDialog, setTemplateDialog] = useState(null);
  const [templateForm, setTemplateForm] = useState({ customerName: "", message: "", orderNumber: "", orderTotal: "", items: "" });
  const [templateError, setTemplateError] = useState("");
  const [templateSending, setTemplateSending] = useState(false);

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
  };

  const loadMessages = useCallback(async () => {
    if (!phone) return;
    try {
      const data = await chat.getMessages(phone);
      setMessages(data.messages || []);
      if (data.messages?.length > 0) {
        lastIdRef.current = data.messages[data.messages.length - 1].id;
      }
      setTimeout(() => scrollToBottom(false), 50);
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
  }, [phone]);

  useEffect(() => {
    if (phone) {
      setMessages([]);
      lastIdRef.current = 0;
      loadMessages();
    }
  }, [phone, loadMessages]);

  const playNotification = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      gain.gain.value = 0.08;
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    } catch (e) {}
  };

  const pollNewMessages = useCallback(async () => {
    if (!phone || isPollingRef.current) return;
    isPollingRef.current = true;
    try {
      const data = await chat.getNewMessages(phone, lastIdRef.current);
      if (data.messages?.length > 0) {
        let appended = false;
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newMsgs = data.messages.filter((m) => !existingIds.has(m.id));
          if (newMsgs.length === 0) return prev;
          const updated = [...prev, ...newMsgs];
          lastIdRef.current = updated[updated.length - 1].id;
          appended = true;
          return updated;
        });
        if (appended) {
          setTimeout(() => scrollToBottom(true), 50);
          if (data.messages.some((m) => m.direction === "incoming")) {
            playNotification();
          }
        }
      }
    } catch (err) {}
    isPollingRef.current = false;
  }, [phone]);

  useEffect(() => {
    if (!phone) return;
    const interval = setInterval(pollNewMessages, 3000);
    return () => clearInterval(interval);
  }, [phone, pollNewMessages]);

  const handleSend = async () => {
    if (!phone) return;

    const resetHeight = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    };

    if (pendingFile) {
      setSending(true);
      try {
        await chat.sendMedia(phone, pendingFile, input.trim());
        setInput("");
        resetHeight();
        clearPendingFile();
        await pollNewMessages();
        onConversationUpdate();
      } catch (err) {
        showToast("Failed to send file", "error");
      }
      setSending(false);
      return;
    }

    const text = input.trim();
    if (!text) return;

    setSending(true);
    setInput("");
    resetHeight();
    try {
      await chat.sendMessage(phone, text);
      await pollNewMessages();
      onConversationUpdate();
    } catch (err) {
      showToast("Failed to send message", "error");
      setInput(text);
    }
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearPendingFile = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingPreview(null);
    setPendingFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
      setPendingFile(file);
      if (file.type.startsWith("image/")) {
        setPendingPreview(URL.createObjectURL(file));
      } else {
        setPendingPreview(null);
      }
    }
    e.target.value = "";
  };

  useEffect(() => {
    return () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    };
  }, [pendingPreview]);

  const windowStatus = useMemo(() => {
    if (!messages.length) return null;
    const lastIncoming = [...messages].reverse().find((m) => m.direction === "incoming");
    if (!lastIncoming) return null;
    const hours = (Date.now() - new Date(lastIncoming.created_at + "Z").getTime()) / 3600000;
    return hours < 24 ? "open" : "closed";
  }, [messages]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    let lastDate = "";
    messages.forEach((msg) => {
      const d = new Date(msg.created_at + "Z");
      if (isNaN(d.getTime())) {
        groups.push({ type: "message", data: msg });
        return;
      }
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      let label;
      const isSame = (a, b) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
      if (isSame(d, today)) label = "Today";
      else if (isSame(d, yesterday)) label = "Yesterday";
      else label = d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });

      if (label !== lastDate) {
        lastDate = label;
        groups.push({ type: "date", date: label });
      }
      groups.push({ type: "message", data: msg });
    });
    return groups;
  }, [messages]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (e.dataTransfer.items?.length > 0) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    dragCounterRef.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(file);
    setPendingPreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
  };

  const openTemplateDialog = (type) => {
    setTemplateForm({ customerName: name || "", message: "", orderNumber: "", orderTotal: "", items: "" });
    setTemplateError("");
    setTemplateDialog(type);
    setTemplateMenuAnchor(null);
  };

  const closeTemplateDialog = () => {
    if (templateSending) return;
    setTemplateDialog(null);
    setTemplateError("");
  };

  const handleTemplateFieldChange = (field) => (e) => {
    setTemplateForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const parseItems = (text) =>
    text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(/^(.+?)\s+x\s+(\d+)$/i);
        return match ? { name: match[1].trim(), quantity: parseInt(match[2], 10) } : { name: line, quantity: 1 };
      });

  const handleTemplateSubmit = async () => {
    setTemplateError("");
    setTemplateSending(true);
    try {
      if (templateDialog === "general") {
        await chat.sendTemplate(phone, templateForm.customerName, templateForm.message);
        showToast("Message sent!", "success");
        await loadMessages();
        onConversationUpdate();
        setTemplateDialog(null);
      } else if (templateDialog === "confirmation") {
        const orderId = templateForm.orderNumber.replace(/^#/, "").trim();
        const data = await ordersApi.resend(orderId);
        if (!data.success) throw new Error(data.error || "Failed to send confirmation");
        showToast(data.message || "Confirmation sent!", "success");
        await loadMessages();
        onConversationUpdate();
        setTemplateDialog(null);
      } else if (templateDialog === "tracking") {
        const orderId = templateForm.orderNumber.replace(/^#/, "").trim();
        const data = await ordersApi.resendTracking(orderId);
        if (!data.success) throw new Error(data.error || "Failed to send tracking update");
        showToast(data.message || "Tracking update sent!", "success");
        await loadMessages();
        onConversationUpdate();
        setTemplateDialog(null);
      }
    } catch (err) {
      setTemplateError(err.response?.data?.message || err.message || "Failed to send message");
    }
    setTemplateSending(false);
  };

  if (!phone) {
    return (
      <div className="chat-panel">
        <div className="chat-empty">
          <div className="chat-empty-icon">
            <ChatIcon sx={{ fontSize: 110 }} />
          </div>
          <h2>CapizFlow Chat</h2>
          <p>
            Select a conversation from the sidebar to start messaging,
            or click the + button to start a new chat.
          </p>
        </div>
      </div>
    );
  }

  const displayName = name || phone;

  return (
    <section
      className="chat-panel"
      aria-label="Chat panel"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="drop-overlay">
          <div className="drop-overlay-inner">
            <AttachFileIcon sx={{ fontSize: 44, color: "var(--wa-teal)" }} />
            <span>Drop file to attach</span>
          </div>
        </div>
      )}
      <div className="chat-header">
        <span className="back-btn-wrap">
          <Tooltip title="Back to chats">
            <IconButton
              size="small"
              onClick={onBack}
              sx={{ color: "#54656f" }}
              aria-label="Back"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </span>
        <Avatar
          sx={{
            bgcolor: avatarColor(phone || displayName),
            width: 40,
            height: 40,
            fontSize: 16,
            fontWeight: 500,
            color: "#fff",
          }}
        >
          {initials(displayName)}
        </Avatar>
        <div className="chat-header-info">
          <div className="chat-header-name">{displayName}</div>
          <div className="chat-header-status">+{phone}</div>
        </div>
        <Button
          size="small"
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
          sx={{
            borderColor: "rgba(7,94,84,0.4)",
            color: "#075e54",
            fontSize: "12px",
            py: 0.5,
            px: 1.5,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "20px",
            flexShrink: 0,
            "&:hover": { borderColor: "#075e54", bgcolor: "rgba(7,94,84,0.06)" },
          }}
        >
          Send Message
        </Button>
        <Menu
          anchorEl={templateMenuAnchor}
          open={Boolean(templateMenuAnchor)}
          onClose={() => setTemplateMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={() => openTemplateDialog("general")}>General</MenuItem>
          <MenuItem onClick={() => openTemplateDialog("confirmation")}>Confirmation</MenuItem>
          <MenuItem onClick={() => openTemplateDialog("tracking")}>Tracking</MenuItem>
        </Menu>
        {windowStatus && (
          <span className={`window-badge window-${windowStatus}`}>
            {windowStatus === "open" ? "24h window open" : "Window closed"}
          </span>
        )}
        <div className="chat-header-actions">
          <Tooltip title="Delete conversation">
            <IconButton
              size="small"
              sx={{ color: "#54656f" }}
              aria-label="Delete conversation"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className="messages-container">
        {groupedMessages.map((item, idx) =>
          item.type === "date" ? (
            <div key={`date-${idx}`} className="message-date-separator">
              <span>{item.date}</span>
            </div>
          ) : (
            <Message key={item.data.id} msg={item.data} />
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      {pendingFile && (
        <div className="attach-preview">
          <div className="attach-preview-inner">
            <div className="attach-preview-thumb">
              {pendingPreview ? (
                <img src={pendingPreview} alt="preview" />
              ) : pendingFile.type.startsWith("video/") ? (
                <MovieIcon />
              ) : pendingFile.type.startsWith("image/") ? (
                <ImageIcon />
              ) : (
                <InsertDriveFileIcon />
              )}
            </div>
            <div className="attach-preview-info">
              <div className="attach-preview-name">{pendingFile.name}</div>
              <div className="attach-preview-size">
                {(pendingFile.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <Tooltip title="Remove">
              <IconButton
                size="small"
                onClick={clearPendingFile}
                sx={{ color: "#d9534f" }}
                aria-label="Remove attachment"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}

      <div className="chat-input-area">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          onChange={handleFileSelect}
        />
        <Tooltip title="Attach file">
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            sx={{ color: "#54656f" }}
            aria-label="Attach file"
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
        <div className="chat-input-wrapper">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              el.style.height = el.scrollHeight + "px";
            }}
            onKeyDown={handleKeyDown}
            placeholder={pendingFile ? "Add a caption..." : "Type a message"}
            rows={1}
          />
        </div>
        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={sending || (!input.trim() && !pendingFile)}
              sx={{
                bgcolor: "#075e54",
                color: "#fff",
                width: 42,
                height: 42,
                "&:hover": { bgcolor: "#054d44" },
                "&.Mui-disabled": {
                  bgcolor: "rgba(7,94,84,0.4)",
                  color: "#fff",
                },
              }}
              aria-label="Send message"
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "17px" }}>Delete conversation?</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: "14px", color: "#667781", margin: 0 }}>
            This will permanently remove the conversation history. This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            sx={{ color: "#54656f", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmDeleteOpen(false);
              try {
                await chat.deleteConversation(phone);
                onBack();
                onConversationUpdate();
              } catch (err) {
                showToast("Failed to delete conversation", "error");
              }
            }}
            sx={{
              bgcolor: "#d9534f",
              "&:hover": { bgcolor: "#c9302c" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(templateDialog)}
        onClose={closeTemplateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: "17px" }}>
          {templateDialog === "general" && "Send General Message"}
          {templateDialog === "confirmation" && "Send Order Confirmation"}
          {templateDialog === "tracking" && "Send Tracking Update"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {templateError && (
            <div className="error-msg" style={{ marginBottom: 0 }}>{templateError}</div>
          )}
          {templateDialog === "general" && (
            <>
              <TextField
                label="Customer Name"
                size="small"
                fullWidth
                autoFocus
                value={templateForm.customerName}
                onChange={handleTemplateFieldChange("customerName")}
              />
              <TextField
                label="Message"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={templateForm.message}
                onChange={handleTemplateFieldChange("message")}
              />
            </>
          )}
          {(templateDialog === "confirmation" || templateDialog === "tracking") && (
            <TextField
              label="Order Number"
              size="small"
              fullWidth
              autoFocus
              placeholder="e.g. 4066"
              value={templateForm.orderNumber}
              onChange={handleTemplateFieldChange("orderNumber")}
              helperText="The backend will fetch all order details and send the message automatically."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={closeTemplateDialog}
            disabled={templateSending}
            sx={{ color: "#54656f", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTemplateSubmit}
            disabled={templateSending}
            sx={{
              bgcolor: "#075e54",
              "&:hover": { bgcolor: "#054d44" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {templateSending ? "Sending…" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default ChatPanel;
